import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Alert,
  CircularProgress, Chip, Box, Typography
} from '@mui/material';

// Return-to-work letter for one absence record. Everything is pre-filled from the record;
// the manager only reviews it. Mount it from any screen that lists absences:
//   <ReturnToWorkLetterDialog open={open} absenceId={id} onClose={() => setOpen(false)} />
export default function ReturnToWorkLetterDialog({ open, absenceId, onClose }) {
  const navigate = useNavigate();
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open || !absenceId) return;
    let cancelled = false;
    setLoading(true); setError(''); setDone(null); setPrefill(null);
    api.get(`/letter-gen/prefill/return-to-work/${absenceId}`)
      .then((res) => {
        if (cancelled) return;
        const p = res.data.prefill;
        setPrefill(p);
        setFields({
          absence_start_date: p.absence_start_date,
          absence_end_date: p.absence_end_date,
          rtw_date: p.rtw_date,
          absence_type: p.absence_type,
          discussion_points: p.discussion_points,
          support_measures: p.support_measures,
          total_absences: p.total_absences,
          total_absence_days: p.total_absence_days,
        });
      })
      .catch((e) => !cancelled && setError(e.response?.data?.error || 'Could not load this absence.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [open, absenceId]);

  const set = (name) => (e) => setFields((f) => ({ ...f, [name]: e.target.value }));

  const handleGenerate = async () => {
    setSaving(true); setError('');
    try {
      const res = await api.post(`/letter-gen/generate/return-to-work/${absenceId}`, fields);
      setDone(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not generate the letter.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Return-to-work letter{prefill ? ` - ${prefill.employee_name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ textAlign: 'center', p: 3 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {done ? (
          <Alert severity="success">
            {done.message}.{' '}
            <Button size="small" onClick={() => { onClose(); navigate('/documents'); }}>Open Document Vault</Button>
          </Alert>
        ) : prefill && (
          <>
            {prefill.warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pre-filled from the absence record. Check the dates and wording, then create the letter;
              it is saved to your Document Vault.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="date" label="Absence from" InputLabelProps={{ shrink: true }}
                           value={fields.absence_start_date || ''} onChange={set('absence_start_date')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="date" label="Absence to" InputLabelProps={{ shrink: true }}
                           value={fields.absence_end_date || ''} onChange={set('absence_end_date')} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" type="date" label="Interview date" InputLabelProps={{ shrink: true }}
                           value={fields.rtw_date || ''} onChange={set('rtw_date')} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Absence recorded as" value={fields.absence_type || ''}
                           onChange={set('absence_type')} inputProps={{ maxLength: 150 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="Absences (52 weeks)"
                           value={fields.total_absences ?? ''} onChange={set('total_absences')}
                           inputProps={{ min: 0, max: 9999 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth size="small" type="number" label="Days (52 weeks)"
                           value={fields.total_absence_days ?? ''} onChange={set('total_absence_days')}
                           inputProps={{ min: 0, max: 9999 }} />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <Chip color="primary"
                      label={`Bradford Factor: ${(Number(fields.total_absences) || 0) ** 2 * (Number(fields.total_absence_days) || 0)}`} />
              </Grid>
              {prefill.interview_notes && fields.discussion_points !== prefill.interview_notes && (
                <Grid item xs={12}>
                  <Alert severity="info" action={
                    <Button color="inherit" size="small"
                            onClick={() => setFields((f) => ({ ...f, discussion_points: prefill.interview_notes }))}>
                      Use as key points
                    </Button>
                  }>
                    Notes are recorded on this absence. Check they are suitable for the employee to read before using them.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={4} label="Key points discussed"
                           value={fields.discussion_points || ''} onChange={set('discussion_points')}
                           inputProps={{ maxLength: 2000 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={2} label="Support measures agreed"
                           value={fields.support_measures || ''} onChange={set('support_measures')}
                           inputProps={{ maxLength: 1000 }} />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
        {!done && (
          <Button variant="contained" onClick={handleGenerate} disabled={saving || loading || !prefill}>
            {saving ? 'Creating...' : 'Create letter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
